import { NODE_PERKS, SECTOR_NAMES } from "../data/perks";
import { DISCOVERABLE_MATS, DEBRIS_ITEMS, TIER1_MATERIALS, TIER2_MATERIALS } from "../data/items";

const STARTER_MATS  = DEBRIS_ITEMS.filter(d => d.category === "starter").map(d => d.id);
const DROP_ONLY     = DEBRIS_ITEMS.filter(d => d.category === "drop_only").map(d => d.id);
const ALL_RAW       = [...STARTER_MATS, ...DROP_ONLY];
const REFINED_T1    = TIER1_MATERIALS.map(m => m.id);
const REFINED_T2    = TIER2_MATERIALS.map(m => m.id);

// Pick `count` distinct items from arr using the seeded rng (no repeats).
function seedPick(arr, count, rng) {
  const pool = [...arr];
  const out = [];
  for (let i = 0; i < Math.min(count, pool.length); i++) {
    const idx = Math.floor(rng() * pool.length);
    out.push(pool[idx]);
    pool.splice(idx, 1);
  }
  return out;
}

export function mulberry32(a) {
  return function () {
    a |= 0; 
    a = (a + 0x6D2B79F5) | 0;
    // Added parentheses around shifts below
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Node archetype definitions ────────────────────────────────────────────────
// Each type has a display label, icon, and color used in the starmap UI.
export const NODE_TYPES = {
  gate_in:        { label: "Gate In",        icon: "⬅",  color: "#22cc77" },
  gate_out:       { label: "Gate Out",       icon: "➡",  color: "#ffaa22" },
  extraction:     { label: "Extraction",     icon: "⛏️",  color: "#4fc3f7" },
  material_site:  { label: "Material Site",  icon: "✨",  color: "#ce93d8" },
  combat_zone:    { label: "Combat Zone",    icon: "⚔️",  color: "#ff5544" },
  fuel_depot:     { label: "Fuel Depot",     icon: "⛽",  color: "#ffd54f" },
  trade_outpost:  { label: "Trade Outpost",  icon: "🏪",  color: "#44ffcc" },
};

export function generateSector(si, sectorDiversity = 0) {
  const rng = mulberry32(si * 7919 + 1337);
  const name = SECTOR_NAMES[si % SECTOR_NAMES.length] +
    (si >= SECTOR_NAMES.length ? ` ${Math.floor(si / SECTOR_NAMES.length) + 1}` : "");

  const nodeCount = Math.floor(rng() * 41) + 40;
  const nodes = [], edges = [];
  const SW = 2400, SH = 2400, M = 120, GM = 60;

  // Gate nodes are always present at fixed left/right positions
  nodes.push({ id: 0, x: GM,      y: SH / 2 + (rng() - 0.5) * 400, type: "gate_in",  label: si === 0 ? "Origin" : "Gate In",  perks: [] });
  nodes.push({ id: 1, x: SW - GM, y: SH / 2 + (rng() - 0.5) * 400, type: "gate_out", label: "Gate Out", perks: [] });

  // Guaranteed fuel depot nodes so the player always has options to refuel.
  // Count scales slightly with sector size so longer sectors remain manageable.
  const fuelDepotCount = 1 + Math.floor(si / 3);
  // 1-2 trade outposts per sector — rare neutral nodes for buying/selling
  const tradeOutpostCount = 1 + (si >= 2 ? 1 : 0);

  let att = 0;
  while (nodes.length < nodeCount && att < nodeCount * 20) {
    att++;
    const nx = M + rng() * (SW - M * 2), ny = M + rng() * (SH - M * 2);
    let close = false;
    for (const n of nodes) {
      if (Math.sqrt((n.x - nx) ** 2 + (n.y - ny) ** 2) < 65) { close = true; break; }
    }
    if (close) continue;

    // Generate perks for this node (0-3 perks)
    const pc = Math.floor(rng() * 4), perks = [], used = new Set();
    for (let p = 0; p < pc; p++) {
      const pk = NODE_PERKS[Math.floor(rng() * NODE_PERKS.length)];
      if (!used.has(pk.id)) { perks.push(pk); used.add(pk.id); }
    }

    // ── Node type probabilities ──────────────────────────────────────────────
    // Combat zones scale up with sector depth; material sites are rare off-path.
    // Fuel depots are first distributed as guaranteed slots, then become rare.
    const placedFuelDepots    = nodes.filter(n => n.type === "fuel_depot").length;
    const placedTradeOutposts = nodes.filter(n => n.type === "trade_outpost").length;
    const roll = rng();
    const combatChance  = Math.min(0.35, 0.15 + si * 0.01);
    // sectorDiversity (0-3) increases material_site and fuel_depot rates, shrinking extraction
    const diversityBonus = Math.min(3, sectorDiversity);
    const materialChance = 0.12 + diversityBonus * 0.04;  // +4% per tier, up to +12%
    const fuelChance    = placedFuelDepots    < fuelDepotCount    ? 0.25 + diversityBonus * 0.05 : 0.04 + diversityBonus * 0.02;
    const tradeChance   = placedTradeOutposts < tradeOutpostCount ? 0.20 : 0.03;

    let nodeType;
    if      (roll < combatChance)                                      nodeType = "combat_zone";
    else if (roll < combatChance + materialChance)                     nodeType = "material_site";
    else if (roll < combatChance + materialChance + fuelChance)        nodeType = "fuel_depot";
    else if (roll < combatChance + materialChance + fuelChance + tradeChance) nodeType = "trade_outpost";
    else                                                               nodeType = "extraction";

    // material_site: assign discoverable mat (round-robin) + include it in pool
    const matId = nodeType === "material_site"
      ? DISCOVERABLE_MATS[nodes.filter(n => n.type === "material_site").length % DISCOVERABLE_MATS.length].id
      : null;

    // Each node gets a seeded drop pool of 2-3 raw items and 1 refined item.
    // extraction: only starter mats + drop_only rares
    // material_site: always includes its discoverable mat + 1-2 starters
    // combat/gate/fuel/trade: 2 starters only (still drops debris when mining)
    // copper + hull_debris are always included — they're the core recipe ingredients.
    let dropPool;
    if (nodeType === "material_site") {
      const extras = seedPick(STARTER_MATS.filter(m => m !== "copper" && m !== "hull_debris"), 1, rng);
      dropPool = [matId, "copper", "hull_debris", ...extras].filter((v, i, a) => a.indexOf(v) === i).slice(0, 4);
    } else if (nodeType === "extraction") {
      const extras = seedPick(ALL_RAW.filter(m => m !== "copper" && m !== "hull_debris"), 2, rng);
      dropPool = ["copper", "hull_debris", ...extras];
    } else {
      const third = seedPick(STARTER_MATS.filter(m => m !== "copper" && m !== "hull_debris"), 1, rng);
      dropPool = ["copper", "hull_debris", ...third];
    }

    // Seeded refined drop: tier-1 for all nodes, tier-2 only from sector 3+
    const refinedPool = si >= 3
      ? seedPick([...REFINED_T1, ...REFINED_T2], 1, rng)
      : seedPick(REFINED_T1, 1, rng);

    nodes.push({ id: nodes.length, x: nx, y: ny, type: nodeType, label: null, perks, matId, dropPool, refinedPool });
  }

  // ── Build adjacency graph ─────────────────────────────────────────────────
  const adj = new Map();
  nodes.forEach(n => adj.set(n.id, new Set()));
  for (const node of nodes) {
    const cur = adj.get(node.id);
    if (cur.size >= 5) continue;
    const mx = Math.floor(rng() * 4) + 1;
    const others = nodes
      .filter(o => o.id !== node.id)
      .map(o => ({ ...o, dist: Math.sqrt((o.x - node.x) ** 2 + (o.y - node.y) ** 2) }))
      .sort((a, b) => a.dist - b.dist);
    let added = 0;
    for (const o of others) {
      if (added >= mx || cur.size >= 5) break;
      if (cur.has(o.id)) continue;
      const oa = adj.get(o.id);
      if (oa.size >= 5 || o.dist > 400) continue;
      cur.add(o.id); oa.add(node.id); edges.push([node.id, o.id]); added++;
    }
  }

  // Ensure full connectivity via BFS — isolated nodes get connected to nearest visited
  const vis = new Set(), q = [0]; vis.add(0);
  while (q.length > 0) {
    const c = q.shift();
    for (const nb of adj.get(c)) { if (!vis.has(nb)) { vis.add(nb); q.push(nb); } }
  }
  for (const node of nodes) {
    if (!vis.has(node.id)) {
      let best = null, bd = Infinity;
      for (const vid of vis) {
        const vn = nodes.find(n => n.id === vid);
        const d = Math.sqrt((vn.x - node.x) ** 2 + (vn.y - node.y) ** 2);
        if (d < bd) { bd = d; best = vid; }
      }
      if (best !== null) {
        adj.get(node.id).add(best); adj.get(best).add(node.id);
        edges.push([node.id, best]); vis.add(node.id); q.push(node.id);
      }
    }
  }

  return { name, nodes, edges, adj, w: SW, h: SH };
}
